import React, { useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { Button } from "reactstrap";
import { Helmet } from "react-helmet";
import Spinner from "../../Spinner/spinner";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { localeEs } from "../../locale/ag-grid-es";
import BreadCrum from "../../breadCrum/breadCrum";
import withTokenCheck from "../../withTokenCheck";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Stack from "@mui/material/Stack";

/* ── Constantes y helpers fuera del componente (no se recrean por render) ── */

const ETIQUETAS = [
    "Inicio Lossman",
    "Cambios De Evento",
    "Reporte de Transportes",
];

const DEFAULT_COL_DEF = {
    sortable: true,
    resizable: true,
    filter: true,
    editable: true,
};

function formatNumeroAcompanantes(element) {
    return element?.data?.formulario?.acompanantes
        ? element.data.formulario.acompanantes.length
        : "";
}

function formatNumeroAcompanantesTransporte(element) {
    return element?.data?.CantidadAcompanantes
        ? element.data.CantidadAcompanantes
        : "No se solicito transporte para acompañantes";
}

const AppAsginacion = ({ token }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [, setModalContent] = useState({});
    const [dateStart, setDateStart] = useState(dayjs());
    const [dateEnd, setDateEnd] = useState(dayjs());
    const gridApi = useRef(null);

    /* Instancia de axios con el token ya configurado */
    const api = useMemo(
        () =>
            axios.create({
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }),
        [token]
    );

    /* ── Totales del reporte (se recalculan solo cuando cambia data) ──────── */
    const totales = useMemo(() => {
        const acc = data.reduce(
            (t, row) => {
                t.paciente += Number(row.Paciente) || 0;
                t.acompanante += Number(row.Acompanante) || 0;
                t.alimentacion += Number(row.Alimentacion) || 0;
                return t;
            },
            { paciente: 0, acompanante: 0, alimentacion: 0 }
        );
        acc.total = acc.paciente + acc.acompanante + acc.alimentacion;
        return acc;
    }, [data]);

    const handleConsult = useCallback(
        (event) => {
            event.preventDefault();
            if (!token) return;

            setLoading(true);
            api.get("/programacionformulario/reporte-transportes", {
                params: {
                    FechaInicio: dateStart.format("YYYY-MM-DD"),
                    FechaFinal: dateEnd.format("YYYY-MM-DD"),
                },
            })
                .then((response) => {
                    setData(response.data ?? []);
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    setLoading(false); // se apaga el spinner pase lo que pase
                });
        },
        [api, token, dateStart, dateEnd]
    );

    const handleModalToggle = useCallback(() => {
        setModalOpen((prev) => !prev);
    }, []);

    const handleButtonClick = useCallback(
        (content) => {
            setModalContent(content);
            handleModalToggle();
        },
        [handleModalToggle]
    );

    const handleEditClick = useCallback(
        (rowData) => {
            api.get(`download/excel_transporte/${rowData.FormularioId}`, {
                headers: {
                    "Content-Type":
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                withCredentials: true,
            })
                .then((response) => {
                    const disposition =
                        response.headers["content-disposition"] || "";
                    const match = disposition.match(/filename="?(.+?)"?$/);
                    const fileName = match
                        ? match[1]
                        : `transporte_${rowData.FormularioId}.xlsx`;

                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(url); // libera memoria
                })
                .catch((error) => {
                    console.error("Error al descargar el archivo:", error);
                });
        },
        [api]
    );

    const handleExportCSV = useCallback(() => {
        gridApi.current?.exportDataAsCsv();
    }, []);

    const onGridReady = useCallback((params) => {
        gridApi.current = params.api;
    }, []);

    /* ── columnDefs memoizado (depende solo de handleEditClick) ───────────── */
    const columnDefs = useMemo(
        () => [
            {
                field: "id",
                headerName: "Descargar Planilla control",
                cellRenderer: (params) => (
                    <Button
                        color="secondary"
                        onClick={() => handleEditClick(params.data)}
                    >
                        Descargar Excel
                    </Button>
                ),
            },
            { headerName: "Numero de caso", field: "formulario.NumeroCaso" },
            { headerName: "Hogar", field: "formulario.hogar.Nombre" },
            { headerName: "Fecha de Servicio", field: "FechaServicio" },
            { headerName: "Tipo de servicio", field: "tipo_servicio_hogar.Texto" },
            { headerName: "Nombre del paciente", field: "formulario.paciente.Nombre" },
            { headerName: "Identificación", field: "formulario.paciente.NumeroIdent" },
            { headerName: "Numero de contacto", field: "formulario.paciente.TelContacto" },
            { headerName: "Ruta de traslado", field: "RutaTraslado" },
            { headerName: "Observaciones", field: "Observacion" },
            { headerName: "Hora de la cita", field: "HoraCita" },
            { headerName: "Hora de salida", field: "HoraSalida" },
            { headerName: "placa del vehiculo", field: "vehiculo.Placa" },
            { headerName: "Nombre del conductor", field: "conductor.Nombre" },
            {
                headerName: "Numero de Acompañantes",
                field: "formulario.acompanantes",
                valueFormatter: formatNumeroAcompanantes,
            },
            {
                headerName: "Numero de acompañantes que se les solicito transporte",
                field: "CantidadAcompanantes",
                valueFormatter: formatNumeroAcompanantesTransporte,
            },
            { headerName: "Transporte Paciente", field: "Paciente" },
            { headerName: "Transporte Acompañante", field: "Acompanante" },
            { headerName: "Transporte Alimentación", field: "Alimentacion" },
            { headerName: "Fecha de ingreso", field: "formulario.FechaInicial" },
            { headerName: "Cita medica", field: "servicio_detalle.tipo_servicio.Texto" },
            { headerName: "Fecha de la cita", field: "servicio_detalle.FechaConsulta" },
            { headerName: "IPS", field: "servicio_detalle.ips.Texto" },
            { headerName: "Usuario creación", field: "created_by.name" },
            { headerName: "Usuario actualización", field: "updated_by.name" },
        ],
        [handleEditClick]
    );

    return (
        <>
            <Helmet>
                <title>Reporte traslados</title>
            </Helmet>
            <div style={{ position: "relative" }}>
                {loading && <Spinner />}
                <div>
                    <BreadCrum etiquetas={ETIQUETAS} />
                </div>
                <div style={{ opacity: loading ? 0.5 : 1 }}>
                    <div className="container-fluid mt-5">
                        <div className="ConsultReport">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Stack spacing={2} sx={{ minWidth: 305 }}>
                                    <DatePicker
                                        label="Fecha Inicial Desde"
                                        id="dateStart"
                                        value={dateStart}
                                        onChange={setDateStart}
                                    />
                                </Stack>
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Stack spacing={2} sx={{ minWidth: 305 }}>
                                    <DatePicker
                                        label="Fecha Inicial Hasta"
                                        value={dateEnd}
                                        onChange={setDateEnd}
                                    />
                                </Stack>
                            </LocalizationProvider>

                            <Button
                                id="buttonConsult"
                                className="mb-3"
                                color="secondary"
                                onClick={handleConsult}
                            >
                                Consultar
                            </Button>
                        </div>

                        <div className="d-flex">
                            <Button
                                color="primary"
                                onClick={() =>
                                    handleButtonClick({
                                        title: "Añadir Traslado",
                                        footer: (
                                            <Button
                                                className="mb-3"
                                                color="secondary"
                                                onClick={handleModalToggle}
                                            >
                                                Cancelar
                                            </Button>
                                        ),
                                    })
                                }
                            >
                                Solicitar traslado
                            </Button>

                            <Button
                                style={{ marginLeft: "10px" }}
                                color="success"
                                onClick={handleExportCSV}
                            >
                                Exportar CSV (Excel)
                            </Button>
                        </div>

                        <div
                            className="mt-3"
                            style={{ height: "500px", width: "100%" }}
                        >
                            <div
                                className="ag-theme-alpine"
                                style={{ height: "100%", width: "100%" }}
                            >
                                <AgGridReact
                                    rowData={data}
                                    columnDefs={columnDefs}
                                    defaultColDef={DEFAULT_COL_DEF}
                                    pagination={true}
                                    onGridReady={onGridReady}
                                    localeText={localeEs}
                                    paginationPageSize={20}
                                />
                            </div>
                        </div>

                        {/* ── Panel de totales debajo de la tabla ───────────── */}
                        <div className="row mt-4 mb-4">
                            <div className="col-md-3 col-sm-6 mb-2">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <h6 className="card-subtitle text-muted">
                                            Total transporte paciente
                                        </h6>
                                        <h3 className="card-title mt-2 mb-0">
                                            {totales.paciente.toLocaleString(
                                                "es-CO"
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6 mb-2">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <h6 className="card-subtitle text-muted">
                                            Total transporte acompañante
                                        </h6>
                                        <h3 className="card-title mt-2 mb-0">
                                            {totales.acompanante.toLocaleString(
                                                "es-CO"
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6 mb-2">
                                <div className="card text-center h-100">
                                    <div className="card-body">
                                        <h6 className="card-subtitle text-muted">
                                            Total transporte alimentación
                                        </h6>
                                        <h3 className="card-title mt-2 mb-0">
                                            {totales.alimentacion.toLocaleString(
                                                "es-CO"
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6 mb-2">
                                <div className="card text-center h-100 border-primary">
                                    <div className="card-body">
                                        <h6 className="card-subtitle text-muted">
                                            Total transporte
                                        </h6>
                                        <h3 className="card-title mt-2 mb-0 text-primary">
                                            {totales.total.toLocaleString(
                                                "es-CO"
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTokenCheck(AppAsginacion);